import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Scales as Scale,
  CircleNotch as Loader2,
  Eye,
  EyeSlash as EyeOff,
  ShieldCheck,
  CalendarBlank as Calendar,
  Gavel,
  ArrowLeft,
  CheckCircle,
  WarningCircle,
} from '@phosphor-icons/react';
import { api, getApiErrorMessage } from '@/lib/api';
import { MIN_PASSWORD_LENGTH } from '@/lib/passwordPolicy';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      setDone(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to reset password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left — dark brand panel */}
        <div className="hidden flex-col justify-between bg-ink-900 p-12 text-ink-200 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
              <Scale className="h-5 w-5" weight="duotone" />
            </div>
            <div className="text-lg font-semibold tracking-tight text-white">Jurivoxa</div>
          </Link>

          <div className="max-w-lg">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-brand-400">
              For barristers, by design
            </div>
            <h1 className="mb-5 text-[34px] font-semibold leading-[1.15] tracking-tight text-white xl:text-[40px]">
              The operating system for the modern barrister.
            </h1>
            <p className="max-w-md text-[15px] leading-relaxed text-ink-300">
              Run your practice yourself — every matter, hearing, judgement, fee
              and document at your fingertips.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-ink-800 pt-8">
              {[
                { icon: <Gavel className="h-4 w-4" />, label: 'Hearings & lists' },
                { icon: <Calendar className="h-4 w-4" />, label: 'Diary & clashes' },
                { icon: <ShieldCheck className="h-4 w-4" />, label: 'BSB compliant' },
              ].map((f) => (
                <div key={f.label} className="flex flex-col gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ink-800 text-brand-400">
                    {f.icon}
                  </div>
                  <div className="text-xs text-ink-300">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-ink-500">
            © {new Date().getFullYear()} Jurivoxa · Built for the Bar
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
                <Scale className="h-5 w-5" weight="duotone" />
              </div>
              <div className="text-lg font-semibold tracking-tight text-foreground">Jurivoxa</div>
            </div>

            {/* ── No token in URL ── */}
            {!token ? (
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                    <WarningCircle className="h-7 w-7 text-red-500" weight="fill" />
                  </div>
                </div>
                <h2 className="mb-2 text-[24px] font-semibold tracking-tight text-foreground">
                  Invalid reset link
                </h2>
                <p className="text-sm text-muted-foreground">
                  This password reset link is missing or invalid. Please request a new one.
                </p>
                <Link
                  to="/forgot-password"
                  className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Request a new link
                </Link>
              </div>
            ) : done ? (
              /* ── Success state ── */
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-500/10">
                    <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" weight="fill" />
                  </div>
                </div>
                <h2 className="mb-2 text-[24px] font-semibold tracking-tight text-foreground">
                  Password updated
                </h2>
                <p className="text-sm text-muted-foreground">
                  Your password has been reset. You can now sign in with your new password.
                </p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              /* ── Form state ── */
              <>
                <div className="mb-6">
                  <h2 className="mb-1 text-[24px] font-semibold tracking-tight text-foreground">
                    Set a new password
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Choose a password with at least {MIN_PASSWORD_LENGTH} characters.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="new-password"
                      className="mb-1.5 block text-sm font-medium text-foreground"
                    >
                      New password
                    </label>
                    <div className="relative">
                      <input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="h-11 w-full rounded-md border border-border bg-card px-3.5 pr-10 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                        placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="confirm-password"
                      className="mb-1.5 block text-sm font-medium text-foreground"
                    >
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-11 w-full rounded-md border border-border bg-card px-3.5 pr-10 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                        placeholder="Repeat your password"
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="rounded-md bg-red-50 dark:bg-red-500/10 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Set new password'
                    )}
                  </button>
                </form>

                <div className="mt-6">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
